import React, { useEffect, useState } from 'react';
import { LabelLarge, FormSection, SectionTitle, NumberInput, Paragraph, LabelWithToolTipCont } from '../../elements';
import styled from 'styled-components';
import ToolTipIcon from './ToolTipIcon';
import MultiRow from './MultiRow';
import YesNoQuestion from './YesNoQuestion';
import { formatCurrency } from '../../helpers/formatNumber';
import { setShowPLV2, updateProfessionalLiability } from '../../actions/application';
import handleFormSubmit from '../../actions/handleFormSubmit';
import {
  selectPlDeductible,
  selectPlLimit,
  selectYearsOfPriorActs,
  selectCertificationsRequired,
  selectCertificationsMaintained,
  selectYearsOfProfessionalExperience,
  selectGrossAnnualSales,
  selectIsYearsOfPriorActsValid,
} from '../../selectors/application';
import { getCurrentPage } from '../../selectors/routing';
import SectionDivider from './SectionDivider';
import { POLICY_OPTIONS, TOOL_TIP_CONTENT_KEY, VALUE_OPTIONS, POLICY_ARIA_LABELS, PAGES } from '../../constants';
import GhostButton from './GhostButton';
import SubmitButton from './SubmitButton';
import { isPageComplete } from '../../selectors/pageComplete';
import { connect } from 'react-redux';
import { debounce } from 'lodash';
import { isPLComplete } from '../../selectors/progress';
import toolTipContent from '../../helpers/toolTipContent';
import { NumberFormatValues } from 'react-number-format';
import AlertIcon from '../../icons/alert.svg?react';
import { useFeatureFlags } from '../../toggle_tools/featureFlagTools';
import usePLLimitOptions from '../../hooks/usePLLimitOptions';
import LoadingSpinnerDots from './LoadingSpinnerDots';
import RetroactiveDate from './RetroactiveDate';

interface ProfessionalLiabilityProps {
  certificationsRequired?: boolean;
  certificationsMaintained?: boolean;
  yearsOfPriorActs?: number;
  yearsOfProfessionalExperience?: number;
  occurrenceLimit?: number;
  deductibleAmount?: number;
  isSummaryPage?: boolean;
  grossAnnualSales?: number;
  onBundleSubmit?: () => void;
  handleFormSubmit: () => void;
  updateProfessionalLiability: (value: any) => void;
  isPageComplete: boolean;
  isPriorActsValid: boolean;
  setShowPLV2?: (value: boolean) => void;
}

const ProfessionalLiability = ({
  certificationsRequired,
  certificationsMaintained,
  yearsOfPriorActs,
  yearsOfProfessionalExperience,
  occurrenceLimit,
  deductibleAmount,
  isSummaryPage,
  grossAnnualSales,
  onBundleSubmit,
  handleFormSubmit,
  updateProfessionalLiability,
  isPageComplete,
  isPriorActsValid,
  setShowPLV2,
}: ProfessionalLiabilityProps) => {
  const { showPlv2 } = useFeatureFlags();
  const { occurrenceLimitOptions, occurrenceToAggregateMap, isLoading: isLoadingPLOptions } = usePLLimitOptions();

  const updateField = (field: string, value?: number | boolean | string) => {
    updateProfessionalLiability({
      grossRevenue: grossAnnualSales || 0,
      [field]: value,
    });
  };

  const updateOccurrenceLimit = (value: number) => {
    updateProfessionalLiability({
      grossRevenue: grossAnnualSales || 0,
      occurrenceLimit: value,
      aggregateLimit: occurrenceToAggregateMap[value],
    });
  };

  const adjustYearsExp = debounce((years: NumberFormatValues) => {
    if (years.floatValue) {
      updateField('yearsOfProfessionalExperience', years.floatValue);
    }
  }, 500);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onBundleSubmit ? onBundleSubmit() : handleFormSubmit();
  };

  const [disabledDeductibleOptions, setDisabledDeductibleOptions] = useState<number[]>([]);

  const adjustDeductibleOptions = (limit: number) => {
    const disabledOptions = VALUE_OPTIONS[POLICY_OPTIONS.DEDUCTIBLE].filter((deductible: number) => {
      return limit < deductible;
    });
    setDisabledDeductibleOptions(disabledOptions);
    if (deductibleAmount && disabledOptions.includes(deductibleAmount)) {
      const deductible = [...VALUE_OPTIONS[POLICY_OPTIONS.DEDUCTIBLE]].reverse().filter((option: number) => {
        return !disabledOptions.includes(option);
      })[0];
      updateField('deductibleAmount', deductible);
    }
  };

  useEffect(() => {
    if (setShowPLV2) setShowPLV2(showPlv2);
  }, [showPlv2, setShowPLV2]);

  return (
    <>
      <form onSubmit={handleSubmit}>
        <FormSection>
          <LabelWithToolTipCont id="PLClaims">
            <LabelLarge display="inline">
              {occurrenceLimitOptions.length === 1
                ? 'For professional liability claims, your business is covered up to the amount of:'
                : 'For professional liability claims, I want my business to be covered up to the amount of:'}
            </LabelLarge>
            <ToolTipIcon
              content={toolTipContent[TOOL_TIP_CONTENT_KEY[POLICY_OPTIONS.PROFESSIONAL_LIABILITY_LIMIT]]}
              toolTipName="Professional_Liability_Limit"
              labelElementId="PLClaims"
            />
          </LabelWithToolTipCont>
          {isLoadingPLOptions ? (
            <LoadingSpinnerDots height={40} width={60} />
          ) : occurrenceLimitOptions.length === 1 ? (
            <SingleLimitValue>{formatCurrency(occurrenceLimitOptions[0])}</SingleLimitValue>
          ) : occurrenceLimitOptions.length > 1 ? (
            <MultiRowWrapper narrow={occurrenceLimitOptions.length === 2}>
              <MultiRow
                money={true}
                options={occurrenceLimitOptions}
                value={occurrenceLimit}
                updateValue={(value) => {
                  adjustDeductibleOptions(value);
                  updateOccurrenceLimit(value);
                }}
                label={POLICY_ARIA_LABELS[POLICY_OPTIONS.PROFESSIONAL_LIABILITY_LIMIT]}
              />
            </MultiRowWrapper>
          ) : null}
        </FormSection>
        {isSummaryPage && <SectionDivider height={20} />}
        <FormSection>
          <LabelWithToolTipCont id="PLDeductible">
            <LabelLarge display="inline">For professional liability claims, I want to pay a deductible of</LabelLarge>
            <ToolTipIcon
              content={toolTipContent[TOOL_TIP_CONTENT_KEY[POLICY_OPTIONS.DEDUCTIBLE]]}
              toolTipName="Deductible"
              labelElementId="PLDeductible"
            />
          </LabelWithToolTipCont>
          <MultiRow
            money={true}
            options={VALUE_OPTIONS[POLICY_OPTIONS.DEDUCTIBLE]}
            value={deductibleAmount}
            updateValue={(value) => updateField('deductibleAmount', value)}
            label={POLICY_ARIA_LABELS[POLICY_OPTIONS.DEDUCTIBLE]}
            disabledOptions={disabledDeductibleOptions}
          />
        </FormSection>
        {isSummaryPage && <SectionDivider height={20} />}
        {!showPlv2 && (
          <FormSection>
            <LabelLarge display="inline">
              How long would you like to be covered for work that your business performed in the past?
            </LabelLarge>
            <MultiRow
              options={VALUE_OPTIONS[POLICY_OPTIONS.YEARS_OF_PRIOR_ACTS]}
              value={yearsOfPriorActs}
              updateValue={(value) => updateField('yearsOfPriorActs', value)}
              label={POLICY_ARIA_LABELS[POLICY_OPTIONS.YEARS_OF_PRIOR_ACTS]}
            />
            {!isPriorActsValid && typeof yearsOfPriorActs !== 'undefined' && (
              <ErrorContainer>
                <ErrorIcon />
                <ErrorText>Prior Acts coverage period cannot exceed the age of the business.</ErrorText>
              </ErrorContainer>
            )}
          </FormSection>
        )}
        <RetroactiveDate />
        <SectionDivider height={20} />
        {!isSummaryPage && (
          <>
            <StyledSectionTitle>Professional Experience</StyledSectionTitle>
            <StyledParagraph>
              These questions are designed to gauge your qualification and experience in your industry.
            </StyledParagraph>
          </>
        )}
        <YesNoQuestion
          question="Does your industry require professional certifications?"
          value={certificationsRequired}
          selectYes={() => updateField('certificationsRequired', true)}
          selectNo={() => updateField('certificationsRequired', false)}
          cyLabel="certificationsRequired"
        />
        {isSummaryPage && <SectionDivider height={20} />}
        <YesNoQuestion
          question="Do you maintain professional certifications?"
          value={certificationsMaintained}
          selectYes={() => updateField('certificationsMaintained', true)}
          selectNo={() => updateField('certificationsMaintained', false)}
          cyLabel="certificationsMaintained"
        />
        {isSummaryPage && <SectionDivider height={20} />}
        <FormSection>
          <LabelLarge display="inline">How many years of professional experience do you have?</LabelLarge>
          <NumberInput
            value={yearsOfProfessionalExperience}
            onValueChange={adjustYearsExp}
            customwidth="quarter"
            data-cy={'years-of-professional-experience'}
            aria-label="Years of professional experience"
          />
        </FormSection>
        {isSummaryPage ? (
          <>
            <SectionDivider height={20} />
            <SubmitButton active={isPageComplete} arrowLinks={false}>
              Add Policy
            </SubmitButton>
          </>
        ) : (
          <GhostButton />
        )}
      </form>
    </>
  );
};

const mapStateToProps = (state: ReduxState) => {
  const page = getCurrentPage(state);
  const isSummaryPage = page === PAGES.SUMMARY;

  return {
    deductibleAmount: selectPlDeductible(state),
    occurrenceLimit: selectPlLimit(state),
    yearsOfPriorActs: selectYearsOfPriorActs(state),
    certificationsRequired: selectCertificationsRequired(state),
    certificationsMaintained: selectCertificationsMaintained(state),
    yearsOfProfessionalExperience: selectYearsOfProfessionalExperience(state),
    grossAnnualSales: selectGrossAnnualSales(state),
    isSummaryPage,
    isPageComplete: isSummaryPage ? isPLComplete(state, { ignoreDefaults: true }) : isPageComplete(state),
    isPriorActsValid: selectIsYearsOfPriorActsValid(state),
  };
};

const MultiRowWrapper = styled.div<{ narrow: boolean }>`
  max-width: ${({ narrow }) => (narrow ? '300px' : 'none')};

  @media (max-width: 850px) {
    margin: 0 auto;
  }
`;

const SingleLimitValue = styled.span`
  font-family: ${({ theme }) => theme.font.typeface.primary};
  font-size: 16px;
  font-weight: bold;
  color: ${({ theme }) => theme.colors.tertiary.zero};
  display: block;
  margin: 8px 0 16px;

  @media (max-width: 767px) {
    font-size: 19px;
    text-align: center;
  }
`;

const StyledSectionTitle = styled(SectionTitle)`
  margin-bottom: 8px;
  padding-top: 20px;
`;

const StyledParagraph = styled(Paragraph)`
  margin-bottom: 32px;
`;

const ErrorIcon = styled(AlertIcon)`
  width: 16px;
  min-width: 16px;
  height: 16px;
  display: block;

  g {
    fill: ${(props) => props.theme.colors.alert.zero};
  }
`;

const ErrorContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 0 0 16px;
  width: 100%;
`;

const ErrorText = styled.p`
  color: ${(props) => props.theme.colors.alert.zero};
  font-family: ${(props) => props.theme.font.typeface.primary};
  font-size: 12px;
  font-style: normal;
  font-weight: 600;
  line-height: 16px;
  letter-spacing: 0.09px;
  margin: 0;
`;

export default connect(mapStateToProps, {
  updateProfessionalLiability,
  handleFormSubmit,
  setShowPLV2,
})(ProfessionalLiability);
